import React, { createContext, useContext, useState, useEffect } from 'react';
import pb from '../services/pocketbaseClient';
import ErrorHandler from '../services/errorHandler';
import AccountManager from '../services/accountManager';
import AdminNotificationService from '../services/adminNotificationService';
import LoginTransition from '../components/LoginTransition/LoginTransition';
import { TOS_CONSTANTS } from '../constants/tosVersion';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // TD3-122.1: Account recovery state
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [pendingRecoveryUser, setPendingRecoveryUser] = useState(null);
  // TD3-105: Login transition state
  const [showLoginTransition, setShowLoginTransition] = useState(false);
  // TD3-129: Email verification modal state (Gate 1)
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');
  // TD3-129: Admin approval modal state (Gate 2)
  const [showPendingApprovalModal, setShowPendingApprovalModal] = useState(false);
  const [pendingApprovalEmail, setPendingApprovalEmail] = useState('');

  // TD3-199: Helper function to clear oracle-related localStorage (LGPD compliance)
  const clearOracleLocalStorage = () => {
    try {
      const keysToRemove = [];

      // Find all oracle session keys and Claude API cache keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('oracle_') || key.startsWith('claudeAPIState_'))) {
          keysToRemove.push(key);
        }
      }

      // Remove all oracle-related keys
      keysToRemove.forEach(key => localStorage.removeItem(key));

      if (keysToRemove.length > 0) {
        console.log(`🧹 Cleared ${keysToRemove.length} oracle localStorage items for privacy protection`);
      }
    } catch (error) {
      console.warn('Failed to clear oracle localStorage:', error);
      // Non-blocking - privacy cleanup failure shouldn't break auth flow
    }
  };

  // Check if user is already authenticated on app start
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // PocketBase automatically persists auth state
        if (pb && pb.authStore && pb.authStore.isValid) {
          // TD3-129: Fetch full user record including custom fields (approvalStatus, etc.)
          const userData = await pb.collection('OracleUsers').getOne(pb.authStore.model.id);
          setUser(userData);
        }
      } catch (error) {
        console.warn('PocketBase auth check failed, using mock mode:', error.message);
        // Fire and forget error capture (no await in useEffect)
        ErrorHandler.captureError(
          ErrorHandler.ERROR_TYPES.AUTH,
          `PocketBase auth check failed: ${error.message}`,
          null,
          { errorStack: error.stack, fallbackMode: 'mock' }
        ).catch(err => console.warn('Failed to capture auth check error:', err));

        // Fall back to mock authentication for development
        const mockUser = localStorage.getItem('mock_auth_user');
        if (mockUser) {
          setUser(JSON.parse(mockUser));
        }
      } finally {
        setLoading(false);
      }
    };

    // Call the async initialization function
    initializeAuth();
  }, []);

  // TD3-129: Listen for cross-tab logout events (email change)
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;

    const channel = new BroadcastChannel('auth_channel');

    channel.onmessage = (event) => {
      if (event.data.type === 'LOGOUT') {
        console.log(`📢 Received cross-tab logout request: ${event.data.reason}`);
        logout();
        // Navigate to login page if using react-router
        window.location.href = '/auth';
      }
    };

    return () => {
      channel.close();
    };
  }, []);

  // Translate common error messages to Portuguese
  const translateError = (errorMessage) => {
    const translations = {
      'Failed to authenticate.': 'Falha na autenticação. Verifique suas credenciais.',
      'The request body is not a valid JSON.': 'Dados inválidos fornecidos.',
      'Failed to create record.': 'Falha ao criar usuário.',
      'The record already exists.': 'Email já está em uso.',
      'Invalid email or password.': 'Email ou senha inválidos.',
      'Authentication required.': 'Autenticação necessária.',
      'Unauthorized': 'Não autorizado',
    };
    
    // Check for exact matches first
    if (translations[errorMessage]) {
      return translations[errorMessage];
    }
    
    // Check for partial matches
    for (const [english, portuguese] of Object.entries(translations)) {
      if (errorMessage.includes(english)) {
        return portuguese;
      }
    }
    
    return errorMessage; // Return original if no translation found
  };

  // TD3-122.1: Account recovery handler
  const handleRecoverAccount = async () => {
    if (!pendingRecoveryUser) return;

    try {
      const result = await AccountManager.recoverAccount(pendingRecoveryUser.id);

      if (result.success) {
        // Refresh user to get updated status
        const updatedUser = await pb.collection('OracleUsers').getOne(pendingRecoveryUser.id);
        setUser(updatedUser);
        setPendingRecoveryUser(null);
        setShowRecoveryModal(false);
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Recovery failed:', error);
      return { success: false, error: 'Erro ao recuperar conta' };
    }
  };

  const handleCancelRecovery = () => {
    // User chose not to recover - proceed with logout
    logout();
    setShowRecoveryModal(false);
    setPendingRecoveryUser(null);
  };

  // Login function
  // TD3-129: Two-stage gate system: Email verification + Admin approval
  // TD3-122.1: Checks for pending deletion and offers recovery
  const login = async (email, password) => {
    try {
      const authData = await pb.collection('OracleUsers').authWithPassword(email, password);

      // TD3-129: GATE 1 - Check if email is verified
      if (!authData.record.verified) {
        // TD3-191: Removed email from log to prevent PII exposure
        console.log(`⚠️ Login attempt by unverified user`);

        // Show email verification modal
        setPendingVerificationEmail(email);
        setShowEmailVerificationModal(true);

        // Logout immediately
        pb.authStore.clear();
        setUser(null);

        return {
          success: false,
          error: 'Por favor, verifique seu e-mail antes de fazer login.'
        };
      }

      // TD3-129: GATE 2 - Check if account is approved by admin
      if (authData.record.approvalStatus !== 'approved') {
        // TD3-191: Removed email from log to prevent PII exposure
        console.log(`⚠️ Login attempt by pending approval user: ${authData.record.id}`);

        // Show pending approval modal
        setPendingApprovalEmail(email);
        setShowPendingApprovalModal(true);

        // Logout immediately
        pb.authStore.clear();
        setUser(null);

        // TD3-129: Notify admin of login attempt by pending user (non-blocking)
        try {
          await AdminNotificationService.notifyPendingLoginAttempt(email);
        } catch (notificationError) {
          // Log but don't fail login flow
          console.warn('Admin login reminder failed:', notificationError);
        }

        return {
          success: false,
          error: 'Sua conta está aguardando aprovação da equipe.'
        };
      }

      // TD3-122: Check if account is pending deletion
      if (AccountManager.isAccountPendingDeletion(authData.record)) {
        const daysRemaining = AccountManager.getDaysRemainingForRecovery(authData.record.deletionRequestedAt);

        if (daysRemaining > 0) {
          // Account can be recovered - show modal
          setPendingRecoveryUser(authData.record);
          setShowRecoveryModal(true);
          return {
            success: true,
            user: authData.record,
            pendingDeletion: true,
            daysRemaining
          };
        } else {
          // Recovery period expired - account should be deleted
          pb.authStore.clear();
          return {
            success: false,
            error: 'Sua conta foi permanentemente excluída. Entre em contato com o suporte se precisar de ajuda.'
          };
        }
      }

      // TD3-199: Cross-user protection - clear localStorage if different user logs in (LGPD Article 46)
      try {
        const previousUserId = localStorage.getItem('lastLoggedInUserId');
        if (previousUserId && previousUserId !== authData.record.id) {
          console.log(`🔒 Different user detected - clearing previous user's oracle data for privacy`);
          clearOracleLocalStorage();
        }
        localStorage.setItem('lastLoggedInUserId', authData.record.id);
      } catch (storageError) {
        // Non-blocking - privacy cleanup failure shouldn't break login
        console.warn('Failed to manage cross-user localStorage cleanup:', storageError);
      }

      setUser(authData.record);
      // TD3-105: Trigger login transition before navigating to dashboard
      setShowLoginTransition(true);
      // TD3-123: User may be unverified - Dashboard will show VerificationRequired component
      return { success: true, user: authData.record };
    } catch (error) {
      console.error('Login error:', error);

      // Capture authentication error
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.AUTH,
        `Login failed for ${email}: ${error.message}`,
        null,
        { email, errorStack: error.stack, attemptTime: new Date().toISOString() }
      );

      // TD3-191: Removed hardcoded test credentials
      // Development environment connects to production PocketBase, so use real test@oracle.com account

      const translatedError = translateError(error.message);
      return {
        success: false,
        error: translatedError || 'Falha no login'
      };
    }
  };

  // Register function
  // TD3-123: Email verification enforcement
  // TD3-151: Terms of Service acceptance tracking
  const register = async (email, password, confirmPassword, nickname = '', tosAccepted = false) => {
    try {
      // Validate passwords match
      if (password !== confirmPassword) {
        return { success: false, error: 'Senhas não coincidem' };
      }

      // Create new user
      // TD3-123: PocketBase automatically sends verification email when user is created
      // (if email verification is enabled in collection settings)
      const userData = {
        email: email,
        password: password,
        passwordConfirm: confirmPassword,
        userType: 'client', // Default to client
        isActive: true,
        oracleLimit: 3, // Default oracle limit
        oraclesUsedThisPeriod: 0,
        currentPeriodStart: new Date().toISOString(),
        // TD3-129: Admin approval workflow - new users start as pending
        approvalStatus: 'pending',
        verified: false, // Explicitly set to false (PocketBase will manage email verification)
        // TD3-122: Account status for LGPD deletion tracking
        accountStatus: 'active', // New accounts start as active (vs. pending_deletion)
        // TD3-151: Terms of Service acceptance tracking
        tosAcceptedAt: tosAccepted ? new Date().toISOString() : null,
        tosVersion: tosAccepted ? TOS_CONSTANTS.version : null // Current ToS version (semantic versioning)
      };

      // Add nickname if provided
      if (nickname && nickname.trim() !== '') {
        userData.nickname = nickname.trim();
      }

      const createdUser = await pb.collection('OracleUsers').create(userData);

      // TD3-129: Request email verification (PocketBase doesn't auto-send)
      try {
        await pb.collection('OracleUsers').requestVerification(email);
        // TD3-191: Removed email from log to prevent PII exposure
        console.log(`✅ Verification email sent to new user`);
      } catch (verificationError) {
        console.error('Failed to send verification email:', verificationError);
        // Don't fail registration if verification email fails
      }

      // TD3-129: Admin notification will be sent AFTER email verification
      // (moved to EmailVerification.js to notify admin only after user verifies email)

      // TD3-129: Do NOT auto-login after registration
      // User must verify email first (Gate 1), then get admin approval (Gate 2)
      // Return success without setting user (user remains logged out)
      return {
        success: true,
        message: 'Conta criada com sucesso! Verifique seu e-mail para ativar sua conta (o link expira em 2 horas). Após verificação, sua conta será analisada pela equipe (até 24 horas).'
      };
    } catch (error) {
      console.error('Registration error:', error);
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.AUTH,
        `Registration failed for ${email}: ${error.message}`,
        null,
        { email, errorStack: error.stack, attemptTime: new Date().toISOString() }
      );
      const translatedError = translateError(error.message);
      return {
        success: false,
        error: translatedError || 'Falha no cadastro'
      };
    }
  };

  // Logout function
  const logout = () => {
    try {
      if (pb && pb.authStore) {
        pb.authStore.clear();
      }
    } catch (error) {
      console.warn('PocketBase logout failed, clearing mock auth:', error.message);
      // Fire and forget error capture (logout should not be blocked)
      ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.AUTH,
        `Logout failed: ${error.message}`,
        user?.id,
        { errorStack: error.stack, fallbackMode: 'mock' }
      ).catch(err => console.warn('Failed to capture logout error:', err));
    }

    // TD3-199: Clear all oracle data on logout (LGPD Article 46 - privacy protection)
    clearOracleLocalStorage();

    // Clear mock authentication and user tracking
    localStorage.removeItem('mock_auth_user');
    localStorage.removeItem('lastLoggedInUserId');
    setUser(null);
  };

  // TD3-129: Request email verification (resend verification email)
  const requestVerification = async (email) => {
    try {
      await pb.collection('OracleUsers').requestVerification(email);
      // TD3-191: Removed email from log to prevent PII exposure
      console.log(`✅ Verification email resent successfully`);
      return { success: true };
    } catch (error) {
      console.error('Request verification error:', error);
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.AUTH,
        `Verification request failed for ${email}: ${error.message}`,
        null,
        { email, errorStack: error.stack }
      );
      return {
        success: false,
        error: 'Erro ao enviar e-mail de verificação'
      };
    }
  };

  // Get current user data (refreshed from server)
  const refreshUser = async () => {
    try {
      if (pb.authStore.isValid) {
        const userData = await pb.collection('OracleUsers').getOne(pb.authStore.model.id);
        setUser(userData);
        return userData;
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.AUTH,
        `Failed to refresh user: ${error.message}`,
        user?.id,
        { errorStack: error.stack }
      );
      logout(); // Clear invalid session
    }
  };

  // TD3-123: Resend email verification
  const resendVerification = async (email) => {
    try {
      await pb.collection('OracleUsers').requestVerification(email);
      return { success: true };
    } catch (error) {
      console.error('Resend verification error:', error);
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.AUTH,
        `Resend verification failed for ${email}: ${error.message}`,
        user?.id,
        { email, errorStack: error.stack }
      );
      return {
        success: false,
        error: error.message || 'Erro ao enviar email de verificação'
      };
    }
  };

  // FUTURE FEATURE: Password reset functionality - commented out for Phase 1 MVP
  // Request password reset
  // const requestPasswordReset = async (email) => {
  //   try {
  //     await pb.collection('OracleUsers').requestPasswordReset(email);
  //     return { success: true };
  //   } catch (error) {
  //     console.error('Password reset request error:', error);
  //     return {
  //       success: false,
  //       error: error.message || 'Erro ao solicitar recuperação de senha'
  //     };
  //   }
  // };

  // // Confirm password reset
  // const confirmPasswordReset = async (token, newPassword, confirmPassword) => {
  //   try {
  //     await pb.collection('OracleUsers').confirmPasswordReset(
  //       token,
  //       newPassword,
  //       confirmPassword
  //     );
  //     return { success: true };
  //   } catch (error) {
  //     console.error('Password reset confirmation error:', error);
  //     return {
  //       success: false,
  //       error: error.message || 'Erro ao redefinir senha'
  //     };
  //   }
  // };

  const value = {
    user,
    login,
    register,
    logout,
    refreshUser,
    resendVerification,
    requestVerification, // TD3-129: Request email verification function
    // TD3-122.1: Account recovery functions
    handleRecoverAccount,
    handleCancelRecovery,
    showRecoveryModal,
    pendingRecoveryUser,
    // TD3-105: Login transition state
    showLoginTransition,
    setShowLoginTransition,
    // TD3-129: Email verification modal state (Gate 1)
    showEmailVerificationModal,
    setShowEmailVerificationModal,
    pendingVerificationEmail,
    // TD3-129: Admin approval modal state (Gate 2)
    showPendingApprovalModal,
    setShowPendingApprovalModal,
    pendingApprovalEmail,
    // FUTURE FEATURE: Password reset functions - commented out for Phase 1 MVP
    // requestPasswordReset,
    // confirmPasswordReset,
    loading,
    isAuthenticated: !!user,
    isVerified: user?.verified || false // TD3-123: Email verification status
  };

  return (
    <AuthContext.Provider value={value}>
      {children}

      {/* TD3-105: Login Transition Overlay */}
      {showLoginTransition && (
        <LoginTransition onComplete={() => setShowLoginTransition(false)} />
      )}

      {/* TD3-122: Account Recovery Modal */}
      {showRecoveryModal && pendingRecoveryUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(8px)'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            border: '2px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '2.5rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            color: 'white',
            fontFamily: 'var(--font-primary)'
          }}>
            <h2 style={{
              fontSize: '1.75rem',
              marginBottom: '1.5rem',
              fontWeight: 600,
              textAlign: 'center'
            }}>
              Recuperar Conta?
            </h2>
            <p style={{
              fontSize: '1rem',
              lineHeight: 1.6,
              marginBottom: '1.5rem',
              textAlign: 'center',
              color: 'rgba(255, 255, 255, 0.8)'
            }}>
              Sua conta estava programada para exclusão. Você tem{' '}
              <strong>
                {pendingRecoveryUser?.deletionRequestedAt
                  ? `${AccountManager.getDaysRemainingForRecovery(pendingRecoveryUser.deletionRequestedAt)} dias`
                  : '30 dias'
                }
              </strong>{' '}
              restantes para recuperá-la.
            </p>
            <p style={{
              fontSize: '0.95rem',
              marginBottom: '2rem',
              textAlign: 'center',
              color: 'rgba(255, 255, 255, 0.7)'
            }}>
              Deseja cancelar a exclusão e restaurar sua conta?
            </p>
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleCancelRecovery}
                style={{
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                Não, continuar exclusão
              </button>
              <button
                onClick={handleRecoverAccount}
                style={{
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  borderRadius: '8px',
                  background: '#10B981',
                  border: '2px solid #059669',
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                Sim, recuperar minha conta
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};